import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { SidebarProvider } from "@/components/ui/sidebar";
import LessonHeader from "@/components/lesson/LessonHeader";
import { Tabs } from "@/components/ui/tabs";
import Sidebar from "@/components/Sidebar";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { sampleClassrooms } from "@/utils/studentUtils";
import { useGetClasses, useGetClass, useSendEmails } from "@/api/classes";
import { COURSES } from "@/data/courseSections";
import { ClassTabs } from "@/components/progress/ClassTabs";
import { StudentsTable } from "@/components/progress/StudentsTable";

const StudentProgress = () => {
  const navigate = useNavigate();
  const { courseSlug, sectionId, lessonId } = useParams();

  const course = COURSES.find((course) => course.slug === courseSlug);

  const classesQuery = useGetClasses(course?.id || "");
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (
      classesQuery.isSuccess &&
      classesQuery.data.count > 0 &&
      selectedClassroomId == null
    ) {
      setSelectedClassroomId(classesQuery.data.items[0].id);
    }
  }, [classesQuery]);
  const classQuery = useGetClass(course?.id || "", selectedClassroomId);

  const sectionIndex = course?.course.findIndex(
    (section) => section.slug === sectionId,
  );
  if (sectionIndex < 0) {
    return null;
  }
  const section = course?.course[sectionIndex];
  const lessonIndex = section?.lessons.findIndex(
    (lesson) => lesson.slug === lessonId,
  );
  if (lessonIndex < 0) {
    return null;
  }
  const lesson = section?.lessons[lessonIndex];

  const sendEmailsMutation = useSendEmails(course?.id || "");

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const openLaunchDialog = () => {
    setConfirmDialogOpen(true);
  };

  const launchCodingWorkspaces = () => {
    if (!!selectedClassroomId) {
      sendEmailsMutation.mutate({ classId: selectedClassroomId });
    }
    setConfirmDialogOpen(false);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen w-full flex flex-col bg-gray-50">
        <Header />
        <div className="flex-1 flex">
          <Sidebar />
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-5xl mx-auto w-full">
              <LessonHeader />
              {classesQuery.isSuccess ? (
                <>
                  <Tabs
                    value={selectedClassroomId}
                    onValueChange={setSelectedClassroomId}
                    className="w-full"
                  >
                    <ClassTabs
                      classrooms={classesQuery.data.items}
                      selectedClassId={selectedClassroomId}
                      onClassChange={setSelectedClassroomId}
                    />
                  </Tabs>

                  <div className="flex mt-4 mb-4">
                    <div className="flex items-center gap-4">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            className="bg-purple-700 hover:bg-purple-800"
                            onClick={openLaunchDialog}
                          >
                            Email Coding Workspaces
                            <Info className="h-4 w-4 ml-2" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            Email coding workspaces will email your students
                            with a link to access interactive exercises for this
                            lesson
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-2 border-b">
                        <h3 className="font-medium">Student Progress</h3>
                      </div>
                      {classQuery.isSuccess && (
                        <StudentsTable
                          students={classQuery.data.items}
                          workspacesActive={true}
                        />
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email interactive lesson content?</DialogTitle>
            <DialogDescription>
              Are you ready to email students a link to the coding workspaces
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={launchCodingWorkspaces}
              className="bg-purple-700 hover:bg-purple-800"
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default StudentProgress;
